/**
 * Routine Guide Module
 * Handles all routine guide functionality including AM/PM routines, 
 * product cards, customization, and interactive features
 */

/**
 * Sets up AM/PM sub-tab navigation for the Routine Guide
 */
function setupRoutineGuideSubTabs() {
    const routineGuideTab = document.getElementById('routineGuide');
    if (!routineGuideTab) return;

    // Create sub-tab navigation if it doesn't exist
    let subTabNav = routineGuideTab.querySelector('.routine-guide-sub-tabs');
    if (!subTabNav) {
        subTabNav = document.createElement('div');
        subTabNav.className = 'routine-guide-sub-tabs';
        
        subTabNav.innerHTML = `
            <div class="sub-tab-triggers">
                <button class="sub-tab-trigger active" data-subtab="am">AM Routine</button>
                <button class="sub-tab-trigger" data-subtab="pm">PM Routine</button>
            </div>
        `;
        
        routineGuideTab.insertBefore(subTabNav, routineGuideTab.firstChild);
    }

    // Create content containers if they don't exist
    let amContent = routineGuideTab.querySelector('#am-routine-content');
    if (!amContent) {
        amContent = document.createElement('div');
        amContent.id = 'am-routine-content';
        amContent.className = 'sub-tab-content active';
        routineGuideTab.appendChild(amContent);
    }

    let pmContent = routineGuideTab.querySelector('#pm-routine-content');
    if (!pmContent) {
        pmContent = document.createElement('div');
        pmContent.id = 'pm-routine-content';
        pmContent.className = 'sub-tab-content';
        pmContent.style.display = 'none';
        routineGuideTab.appendChild(pmContent);
    }

    // Add click handlers for sub-tabs
    const subTabTriggers = subTabNav.querySelectorAll('.sub-tab-trigger');
    subTabTriggers.forEach(trigger => {
        trigger.addEventListener('click', () => {
            // Remove active class from all triggers and contents
            subTabTriggers.forEach(t => {
                t.classList.remove('active');
            });
            
            // Hide all content areas
            routineGuideTab.querySelectorAll('.sub-tab-content').forEach(content => {
                content.style.display = 'none';
            });
            
            // Activate clicked trigger
            trigger.classList.add('active');
            
            // Show corresponding content
            const targetContent = routineGuideTab.querySelector(`#${trigger.dataset.subtab}-routine-content`);
            if (targetContent) {
                targetContent.style.display = 'block';
            }
        });
    });

    // Set initial active state (CSS handles the styling)
    const activeSubTab = subTabNav.querySelector('.sub-tab-trigger.active');
    // CSS handles the active state styling automatically
}

/**
 * Generates routine guide content based on advanced products
 * @param {Array} advancedProducts - Array of advanced product objects (excluding bundles)
 */
function generateRoutineGuideContent(advancedProducts) {
    const routineGuideTab = document.getElementById('routineGuide');
    if (!routineGuideTab) return;

    // Hide all possible loading indicators
    const loadingSelectors = [
        '.loading-routine-guide',
        '.loading-indicator',
        '[class*="loading"]',
        '[id*="loading"]'
    ];
    
    loadingSelectors.forEach(selector => {
        const loadingElements = routineGuideTab.querySelectorAll(selector);
        loadingElements.forEach(element => {
            element.style.display = 'none';
        });
    });
    
    // Also check for any text content that might be loading messages
    const textNodes = routineGuideTab.querySelectorAll('*');
    textNodes.forEach(node => {
        if (node.textContent && node.textContent.toLowerCase().includes('loading')) {
            node.style.display = 'none';
        }
    });

    // Filter out bundles from advanced products
    const productsForRoutine = advancedProducts.filter(p => p && p.category?.toLowerCase() !== 'bundle');
    
    const amContent = routineGuideTab.querySelector('#am-routine-content');
    const pmContent = routineGuideTab.querySelector('#pm-routine-content');
    
    // Get user's quiz responses for personalization - use correct field names
    const userResponses = window.answers || window.currentQuizData || {};
    console.log(">>> User responses for personalization:", userResponses);
    console.log(">>> window.answers exists:", !!window.answers);
    console.log(">>> window.currentQuizData exists:", !!window.currentQuizData);
    console.log(">>> special_conditions type:", typeof userResponses.special_conditions, userResponses.special_conditions);
    console.log(">>> primary_concerns:", userResponses.primary_concerns);
    console.log(">>> secondary_concerns type:", typeof userResponses.secondary_concerns, userResponses.secondary_concerns);
    
    // Extract personalization flags based on ACTUAL quiz fields
    // Handle both array and string formats for special_conditions
    const specialConditions = userResponses.special_conditions;
    const isSensitiveSkin = (Array.isArray(specialConditions) ? specialConditions.includes('sensitive') : specialConditions === 'sensitive') || 
                           userResponses.primary_concerns === 'sensitivity';
    const isRetinoidBeginner = Array.isArray(specialConditions) ? specialConditions.includes('retinoid') : specialConditions === 'retinoid';
    
    const primaryConcerns = userResponses.primary_concerns;
    const secondaryConcerns = userResponses.secondary_concerns;
    
    const isOilySkin = userResponses.primary_concerns === 'oily' || 
                      (Array.isArray(secondaryConcerns) ? secondaryConcerns.includes('oily') : secondaryConcerns === 'oily');
    const isDryDehydrated = userResponses.primary_concerns === 'dehydrated' || 
                           (Array.isArray(secondaryConcerns) ? secondaryConcerns.includes('dehydrated') : secondaryConcerns === 'dehydrated');
    
    const lifestyle = userResponses.lifestyle;
    const goals = userResponses.goals;
    
    console.log(">>> Personalization flags:", { 
        isSensitiveSkin, 
        isRetinoidBeginner, 
        primaryConcerns, 
        isOilySkin, 
        isDryDehydrated, 
        lifestyle, 
        goals,
        specialConditions,
        secondaryConcerns
    });
    
    // Create helpful tips section that appears at the bottom of both routines
    const hintsAndTips = createSkincareHintsSection(isSensitiveSkin, isRetinoidBeginner, primaryConcerns, isOilySkin, isDryDehydrated, lifestyle, goals);
    
    if (amContent) {
        amContent.innerHTML = generateAMRoutineHTML(productsForRoutine, userResponses) + hintsAndTips;
    }
    
    if (pmContent) {
        pmContent.innerHTML = generatePMRoutineHTML(productsForRoutine, userResponses) + hintsAndTips;
    }
}

/**
 * Creates the skincare hints and tips section with personalized messaging
 * @param {boolean} isSensitiveSkin - Whether user has sensitive skin
 * @param {boolean} isRetinoidBeginner - Whether user is new to retinoids
 * @param {string} primaryConcerns - User's primary skin concerns
 * @param {boolean} isOilySkin - Whether user has oily skin concerns
 * @param {boolean} isDryDehydrated - Whether user has dry/dehydrated skin
 * @param {string} lifestyle - User's lifestyle (dynamic, indoors, wellness)
 * @param {string} goals - User's skincare goals
 * @returns {string} HTML for hints section
 */
/**
 * Creates the skincare hints and tips section with personalized messaging
 * @param {boolean} isSensitiveSkin - Whether user has sensitive skin
 * @param {boolean} isRetinoidBeginner - Whether user is new to retinoids
 * @param {string} primaryConcerns - User's primary skin concerns
 * @param {boolean} isOilySkin - Whether user has oily skin concerns
 * @param {boolean} isDryDehydrated - Whether user has dry/dehydrated skin
 * @param {string} lifestyle - User's lifestyle (dynamic, indoors, wellness)
 * @param {string} goals - User's skincare goals
 * @returns {string} HTML for hints section
 */
function createSkincareHintsSection(isSensitiveSkin, isRetinoidBeginner, primaryConcerns, isOilySkin, isDryDehydrated, lifestyle, goals) {
    // Create personalized intro message based on user's quiz responses using Sachi's empathetic tone
    let personalizedIntro = "Here are some thoughtful tips to support your unique skincare journey:";
    
    if (isSensitiveSkin && isRetinoidBeginner) {
        personalizedIntro = "Since you have sensitive skin and are new to stronger actives, here's how to gently build your routine with care and mindfulness:";
    } else if (isSensitiveSkin) {
        personalizedIntro = "For your sensitive skin, here are some gentle approaches to nurture and support your skin's natural balance:";
    } else if (isRetinoidBeginner) {
        personalizedIntro = "Since you're new to stronger actives like retinoids, here are some thoughtful guidelines to support your skin's journey:";
    } else if (primaryConcerns === 'acne_marks' || primaryConcerns === 'hyperpigmentation') {
        personalizedIntro = "For addressing marks and uneven tone, consistency and patience are your allies. Here are your essential tips:";
    } else if (primaryConcerns === 'sun_damage') {
        personalizedIntro = "For sun-affected skin, here's how to thoughtfully restore and protect your skin's natural radiance:";
    } else if (primaryConcerns === 'fine_lines' || primaryConcerns === 'firmness') {
        personalizedIntro = "For supporting your skin's natural resilience and well-ageing journey, here's how to nurture firmness and vitality:";
    } else if (primaryConcerns === 'texture') {
        personalizedIntro = "For smoothing and refining your skin's texture, here's how to gently encourage renewal:";
    } else if (primaryConcerns === 'sensitivity') {
        personalizedIntro = "For reactive and sensitive skin, here's how to calm, soothe, and strengthen your skin's protective barrier:";
    } else if (primaryConcerns === 'enlarged_pores') {
        personalizedIntro = "For refining the appearance of pores, here's how to support your skin's natural texture:";
    } else if (primaryConcerns === 'dull') {
        personalizedIntro = "For restoring your skin's natural radiance and luminosity, here's how to awaken your glow:";
    } else if (isOilySkin || primaryConcerns === 'congestion' || primaryConcerns === 'oily') {
        personalizedIntro = "For oily or congested skin, here's how to achieve balance while respecting your skin's natural protective mechanisms:";
    } else if (isDryDehydrated || primaryConcerns === 'dehydrated') {
        personalizedIntro = "For dehydrated skin, here's how to restore moisture balance and strengthen your skin's barrier:";
    } else if (lifestyle === 'dynamic') {
        personalizedIntro = "For your active lifestyle, here's how to keep your skin protected, resilient, and balanced:";
    } else if (lifestyle === 'indoors') {
        personalizedIntro = "For indoor living and screen exposure, here's how to support your skin's natural defenses:";
    } else if (lifestyle === 'wellness') {
        personalizedIntro = "For your wellness-focused lifestyle, here's how to align your skincare with your holistic approach to health:";
    } else if (goals === 'barrier') {
        personalizedIntro = "To calm and strengthen your skin's protective barrier, here are your essential guidelines:";
    }

    // Create personalized tips array
    const personalizedTips = [];
    let tipNumber = 1;

    // Add personalized tips based on user profile
    if (isSensitiveSkin || isRetinoidBeginner || isDryDehydrated) {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Your Unique Skin State",
            content: `${isSensitiveSkin ? 'You can layer products over or mix them into your moisturiser if your skin is feeling reactive to low pH skincare or actives. Your skin\'s sensitivity is not a limitation, it\'s guidance for a more thoughtful approach. ' : ''}${isRetinoidBeginner ? 'If you\'re new to stronger actives, begin by limiting use to once a week, gradually increasing frequency as your skin builds tolerance. This patient approach honours your skin\'s natural adaptation process. ' : ''}${isDryDehydrated ? 'Focus on hydrating serums and consider applying treatments over a light moisturiser to prevent irritation. Your skin\'s thirst for moisture is a signal to prioritize barrier support. ' : ''}The Pro Resilience® serum can help support your skin to tolerate more transformational skincare ingredients with greater ease.`,
            class: "sensitive"
        });
    }
    
    if (primaryConcerns === 'hyperpigmentation' || primaryConcerns === 'acne_marks' || primaryConcerns === 'sun_damage') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Uneven Tone & Marks",
            content: "Consistency and patience are your allies in evening skin tone. Use your Triphala Corrector daily and always follow with SPF protection. Real results typically emerge after 4-6 weeks of dedicated care, trust in your skin's natural renewal process.",
            class: "pigmentation"
        });
    }
    
    if (primaryConcerns === 'fine_lines' || primaryConcerns === 'firmness') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Well-Ageing Support",
            content: "Supporting your skin's natural resilience is a journey of consistent care. Focus on strengthening your skin barrier, maintaining hydration, and using targeted treatments that respect your skin's wisdom. Remember: healthy skin ages beautifully.",
            class: "aging"
        });
    }
    
    if (primaryConcerns === 'texture') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Smooth Texture",
            content: "Gentle, consistent exfoliation and barrier support will help refine your skin's natural texture. Avoid over-exfoliating, your skin responds better to thoughtful encouragement than aggressive intervention.",
            class: "texture"
        });
    }
    
    if (primaryConcerns === 'enlarged_pores') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Refined Pores",
            content: "Supporting your skin's natural texture includes gentle exfoliation and consistent hydration. Remember, pores serve an important function, we're working to optimize their appearance, not eliminate them.",
            class: "pores"
        });
    }
    
    if (primaryConcerns === 'dull') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Natural Radiance",
            content: "Your skin's natural luminosity can be awakened through gentle exfoliation, consistent hydration, and protection. Focus on supporting your skin's renewal cycle for that healthy, inner glow.",
            class: "radiance"
        });
    }
    
    if (isOilySkin || primaryConcerns === 'congestion' || primaryConcerns === 'oily') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Oily/Congested Skin",
            content: "Don't skip moisturiser even if your skin feels oily, this can trigger more oil production as your skin tries to compensate. Your skin's oil is protective; we're working to balance it, not eliminate it. Start gentle exfoliation gradually to prevent irritation.",
            class: "oily"
        });
    }
    
    if (primaryConcerns === 'sensitivity') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Reactive Skin",
            content: "Your skin's reactivity is communication, listen to its needs and respond with gentleness. Focus on barrier-strengthening ingredients and avoid potential irritants. Sometimes less is more in supporting sensitive skin.",
            class: "reactive"
        });
    }
    
    if (lifestyle === 'dynamic') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Your Active Lifestyle",
            content: "Your dynamic days require skincare that protects and recovers. Double-cleanse after active days, stay consistent with barrier support, and never skip SPF, even on cloudy days. Your skin is your partner in every adventure.",
            class: "lifestyle"
        });
    }
    
    if (lifestyle === 'indoors') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Indoor Living",
            content: "Blue light and dry indoor air can stress your skin. Focus on hydration, antioxidant protection, and consider gentle movement or fresh air breaks to support your skin's natural circulation and wellbeing.",
            class: "indoor"
        });
    }
    
    if (lifestyle === 'wellness') {
        personalizedTips.push({
            number: tipNumber++,
            title: "For Your Wellness Journey",
            content: "Your holistic approach to health beautifully aligns with mindful skincare. Consider how your inner wellness, nutrition, sleep, stress management, supports your skin's natural balance and radiance.",
            class: "wellness"
        });
    }

    // Add general tips
    const generalTips = [
        {
            number: tipNumber++,
            title: "Always Patch Test",
            content: "Test any new product for at least 24-48 hours by applying a small amount behind your ear, underside of your jaw, corner of your forehead or side of your neck. Listen to how your skin responds and honour its needs.",
            class: "general"
        },
        {
            number: tipNumber++,
            title: "Go Slow",
            content: "Introduce one product at a time to your current routine, starting gradually with as little as once a week. You might experience a gentle tingle with low pH skincare or exfoliants - this should ease as your skin becomes accustomed to the new state of care.",
            class: "general"
        },
        {
            number: tipNumber++,
            title: "Thoughtful Layering",
            content: "Always follow the thinnest to thickest rule when layering. Apply stronger actives to clean, dry skin, ensuring all previous layers are fully absorbed. This mindful approach respects your skin's natural rhythm.",
            class: "general"
        }
    ];

    const allTips = [...personalizedTips, ...generalTips];

    return `
        <div class="routine-guide-skincare-hints">
            <h4 class="routine-guide-hints-header">
                <span class="icon">💡</span>
                Skincare Hints and Tips
            </h4>
            
            <p class="routine-guide-hints-intro">${personalizedIntro}</p>
            
            <div class="routine-guide-tips-container">
                ${allTips.map(tip => `
                    <div class="routine-guide-tip-box ${tip.class}">
                        <div class="routine-guide-tip-number">${tip.number}</div>
                        <div class="routine-guide-tip-title">${tip.title}</div>
                        <p class="routine-guide-tip-content">${tip.content}</p>
                    </div>
                `).join('')}
            </div>
            
            <div class="routine-guide-education-link">
                <strong>Deepen Your Understanding</strong>
                <p>
                    Visit <a href="https://sachiskin.com/pages/skin-education" target="_blank">Sachi Papers Skin Education</a> to explore the wisdom of ancient traditions and modern science for your skincare journey.
                </p>
            </div>
        </div>
    `;
}

/**
 * Creates enhanced product card with stock status, pricing, and add to cart functionality
 * @param {Object} product - Product object
 * @param {string|boolean} customApplicationTips - Custom application tips or true for default tips
 * @returns {string} HTML for product card
 */
function createEnhancedProductCard(product, customApplicationTips = true) {
    const imageUrl = product.imageUrl || 'https://via.placeholder.com/60x60?text=' + encodeURIComponent(product.name);
    
    // Get application instructions
    let applicationTips = '';
    if (customApplicationTips) {
        if (typeof customApplicationTips === 'string') {
            // Use custom provided tips
            applicationTips = customApplicationTips;
        } else {
            // Use default tips based on product type
            if (product.name?.toLowerCase().includes('pro resilience')) {
                applicationTips = 'Apply 1-2 pumps to help firm, calm, and support your skin\'s barrier.';
            } else if (product.name?.toLowerCase().includes('triphala')) {
                applicationTips = 'Apply 1-2 pumps to help fade dark spots and even out your skin tone. Let it absorb fully before applying moisturiser or SPF.';
            } else if (product.name?.toLowerCase().includes('cleanser')) {
                applicationTips = 'Gently massage onto damp skin, then rinse thoroughly with lukewarm water.';
            } else if (product.name?.toLowerCase().includes('accelerator')) {
                applicationTips = 'Use as a flash mask once a week for about 15-20 minutes, then rinse off.';
            } else if (product.name?.toLowerCase().includes('retinal') || product.name?.toLowerCase().includes('ursolic')) {
                applicationTips = 'Apply once a week to support collagen production. Gradually increase frequency.';
            }
        }
    }
    
    // Stock status - only show if out of stock
    const isInStock = product.available !== false; // Assuming product.available exists
    const stockStatus = !isInStock ? 
        '<span class="routine-guide-stock-status out-of-stock">⚠ Out of Stock</span>' : '';
    
    return `
        <div class="routine-guide-product-card">
            <div class="routine-guide-product-image">
                <img src="${imageUrl}" alt="${product.name}" onerror="this.src='https://via.placeholder.com/60x60?text=Product'">
            </div>
            <div class="routine-guide-product-info">
                <div class="routine-guide-product-header">
                    <h5>${product.name}</h5>
                    <div class="routine-guide-product-meta">
                        ${stockStatus}
                    </div>
                </div>
                ${applicationTips ? `<div class="routine-guide-product-application-tips">${applicationTips}</div>` : ''}
                
                <div class="routine-guide-product-actions">
                    ${isInStock ? `
                        <button class="routine-guide-btn-add-cart" 
                                data-variant-id="${product.shopifyVariantId || product.id}" 
                                onclick="handleRoutineGuideAddToCart(${JSON.stringify(product).replace(/"/g, '&quot;')}, event)">
                            Add to Cart
                        </button>
                    ` : `
                        <button class="routine-guide-btn-add-cart" disabled>
                            Out of Stock
                        </button>
                    `}
                </div>
            </div>
        </div>
    `;
}

/**
 * Creates a combined cleanser box containing multiple cleanser products
 * @param {Array} cleansers - Array of cleanser products
 * @returns {string} HTML for combined cleanser box
 */
function createCombinedCleanserBox(cleansers) {
    if (cleansers.length === 0) return '';
    
    // Single how-to-use section for all cleansers
    const applicationTips = '<div class="routine-guide-shared-instructions"><p>Gently massage onto damp skin, then rinse thoroughly with lukewarm water.</p></div>';
    
    // Create individual product cards within the box
    const productCards = cleansers.map(cleanser => {
        const imageUrl = cleanser.imageUrl || 'https://via.placeholder.com/60x60?text=' + encodeURIComponent(cleanser.name);
        const isInStock = cleanser.available !== false;
        const stockStatus = !isInStock ? '<span class="routine-guide-stock-status out-of-stock">⚠ Out of Stock</span>' : '';
        
        return `
            <div class="routine-guide-cleanser-item">
                <div class="routine-guide-product-image">
                    <img src="${imageUrl}" alt="${cleanser.name}" onerror="this.src='https://via.placeholder.com/60x60?text=Product'">
                </div>
                <div class="routine-guide-cleanser-info">
                    <h5>${cleanser.name}</h5>
                    ${stockStatus}
                    <div class="routine-guide-cleanser-button-wrapper">
                        <div class="routine-guide-product-actions">
                            ${isInStock ? `
                                <button class="routine-guide-btn-add-cart" 
                                        data-variant-id="${cleanser.shopifyVariantId || cleanser.id}" 
                                        onclick="handleRoutineGuideAddToCart(${JSON.stringify(cleanser).replace(/"/g, '&quot;')}, event)">
                                    Add to Cart
                                </button>
                            ` : `
                                <button class="routine-guide-btn-add-cart" disabled>
                                    Out of Stock
                                </button>
                            `}
                        </div>
                    </div>
                </div>
            </div>
        `;
    }).join('');
    
    return `
        <div class="routine-guide-cleanser-box">
            <div class="routine-guide-cleanser-products">
                ${productCards}
            </div>
            ${applicationTips}
        </div>
    `;
}

/**
 * Generates AM routine HTML content
 * @param {Array} products - Array of product objects
 * @param {Object} userResponses - User's quiz responses for personalization
 * @returns {string} HTML content for AM routine
 */
function generateAMRoutineHTML(products, userResponses) {
    console.log(">>> generateAMRoutineHTML - Available products:", products.map(p => ({id: p.id, name: p.name, category: p.category})));
    
    // Use same personalization logic as main function
    const specialConditions = userResponses.special_conditions;
    const isSensitiveSkin = (Array.isArray(specialConditions) ? specialConditions.includes('sensitive') : specialConditions === 'sensitive') || 
                           userResponses.primary_concerns === 'sensitivity';
    const isRetinoidBeginner = Array.isArray(specialConditions) ? specialConditions.includes('retinoid') : specialConditions === 'retinoid';
    const primaryConcerns = userResponses.primary_concerns;
    
    // Categorize products correctly - Triphala is a serum, not treatment
    const cleansers = products.filter(p => 
        p.category?.toLowerCase().includes('cleans') || 
        p.name?.toLowerCase().includes('cleanser')
    );
    
    const serums = products.filter(p => 
        p.name?.toLowerCase().includes('serum') || 
        p.name?.toLowerCase().includes('resilience') ||
        p.name?.toLowerCase().includes('triphala') // Triphala is a serum
    );
    
    const treatments = products.filter(p => 
        (p.category?.toLowerCase().includes('treat') && 
         !p.name?.toLowerCase().includes('serum') && 
         !p.name?.toLowerCase().includes('resilience') &&
         !p.name?.toLowerCase().includes('triphala')) ||
        p.category?.toLowerCase().includes('exfoliat') ||
        p.name?.toLowerCase().includes('accelerator') ||
        p.name?.toLowerCase().includes('overnight')
    );
    
    const moisturizers = products.filter(p => 
        p.category?.toLowerCase().includes('moistur') ||
        p.step === 5 || // Step 5 is specifically for moisturizers
        (p.category?.toLowerCase() === 'moisturize') // Exact match for Future Veil category
    );
    
    console.log(">>> Cleansers found:", cleansers.map(p => p.name));
    console.log(">>> Serums found:", serums.map(p => p.name));
    console.log(">>> Treatments found:", treatments.map(p => p.name));
    console.log(">>> Moisturizers found:", moisturizers.map(p => p.name));
    
    let routineHTML = `
        <div class="routine-timeline">
            <div class="timeline-line"></div>
            <h3>☀️ Your Morning Routine</h3>
    `;

    let stepNumber = 1;

    // Step 1: Cleansers (if any)
    if (cleansers.length > 0) {
        const stepId = 'am-cleanse';
        // Find Saponins and Saffron cleansers
        const saponinsCleanser = cleansers.find(c => c.name?.toLowerCase().includes('saponins'));
        const saffronCleanser = cleansers.find(c => c.name?.toLowerCase().includes('saffron'));
        // If both are present, show as options like PM overnight treatment
        let cleanserContent = '';
        if (saponinsCleanser && saffronCleanser) {
            cleanserContent = `
                <div class="routine-guide-treatment-options">
                    <div class="routine-guide-treatment-option">
                        ${createEnhancedProductCard(saponinsCleanser, 'Gently massage onto damp skin, then rinse thoroughly with lukewarm water.')}
                    </div>
                    <div class="routine-guide-treatment-separator"><span>or</span></div>
                    <div class="routine-guide-treatment-option">
                        ${createEnhancedProductCard(saffronCleanser, 'Gently massage onto damp skin, then rinse thoroughly with lukewarm water.')}
                    </div>
                </div>
            `;
        } else if (saponinsCleanser) {
            cleanserContent = createEnhancedProductCard(saponinsCleanser, 'Gently massage onto damp skin, then rinse thoroughly with lukewarm water.');
        } else if (saffronCleanser) {
            cleanserContent = createEnhancedProductCard(saffronCleanser, 'Gently massage onto damp skin, then rinse thoroughly with lukewarm water.');
        } else {
            cleanserContent = createCombinedCleanserBox(cleansers);
        }
        routineHTML += createTimelineStep(stepNumber, stepId, 'Cleanse', 
            'Start with a gentle cleanse to remove impurities and prepare your skin.',
            cleanserContent,
            '#007bff'
        );
        stepNumber++;
    }

    // Step 2: Pro Resilience Serum
    if (serums.length > 0) {
        const proResilience = serums.find(s => s.name?.toLowerCase().includes('pro resilience'));
        
        if (proResilience) {
            const stepId = 'am-pro-resilience';
            let stepDescription = `Prep your skin with barrier support${isSensitiveSkin ? ', especially important for sensitive skin' : ''}.`;
            routineHTML += createTimelineStep(stepNumber, stepId, 'Strengthen & Prep', 
                stepDescription,
                createEnhancedProductCard(proResilience, 'Apply 1-2 pumps to clean, dry skin. Gently pat in until absorbed.'),
                '#007bff'
            );
            stepNumber++;
        }
    }

    // Step 3: Triphala Pigmentation Corrector
    const triphalaSerum = serums.find(s => s.name?.toLowerCase().includes('triphala'));
    if (triphalaSerum) {
        const stepId = 'am-triphala';
        let stepContent = createEnhancedProductCard(triphalaSerum, 'Apply 1-2 pumps. Let it absorb fully before applying moisturiser or SPF.');
        
        if (isSensitiveSkin) {
            stepContent += `
                <div class="routine-guide-tips-section">
                    <div class="routine-guide-tips-title">💡 Tips for Sensitive Skin:</div>
                    <ul class="routine-guide-tips-list">
                        <li>If your skin is feeling sensitive, you can layer the Triphala Pigmentation Corrector over your moisturiser.</li>
                        <li>For sensitive skin, start by using once daily, 2–3 times per week. Gradually increase frequency as your skin builds tolerance.</li>
                    </ul>
                </div>
            `;
        }
        
        routineHTML += createTimelineStep(stepNumber, stepId, 'Target Your Concerns', 
            'Apply your pigmentation corrector to help fade dark spots and even skin tone.',
            stepContent,
            '#007bff'
        );
        stepNumber++;
    }

    // Step 4: Moisturise
    if (moisturizers.length > 0) {
        const stepId = 'am-moisturise';
        let stepContent = '';
        
        // Display all moisturizer products
        moisturizers.forEach(moisturizer => {
            const applicationTips = moisturizer.name?.toLowerCase().includes('future veil') || moisturizer.name?.toLowerCase().includes('peptide') 
                ? 'Apply 1-2 pumps evenly across the face, and one pump to the neck and décolleté. Gently massage until absorbed to lock in hydration and support skin resilience.'
                : 'Apply to clean skin and gently massage until absorbed.';
            
            stepContent += createEnhancedProductCard(moisturizer, applicationTips);
        });
        
        routineHTML += createTimelineStep(stepNumber, stepId, '💧 Moisturise', 
            'Lock in hydration and support your skin barrier.',
            stepContent,
            '#007bff'
        );
        stepNumber++;
    } else {
        // Fallback if no moisturizer products
        routineHTML += createTimelineStep(stepNumber, 'am-moisturise', '💧 Moisturise', 
            'Follow with a moisturiser if needed.',
            '',
            '#007bff'
        );
        stepNumber++;
    }

    // Step 5: SPF
    routineHTML += createTimelineStep(stepNumber + 1, 'am-spf', '☀️ Protect', 
        'Always finish with SPF 30 or higher to protect your skin during the day.',
        '',
        '#ffc107'
    );

    routineHTML += '</div>';
    return routineHTML;
}

/**
 * Generates PM routine HTML content
 * @param {Array} products - Array of product objects
 * @param {Object} userResponses - User's quiz responses for personalization
 * @returns {string} HTML content for PM routine
 */
function generatePMRoutineHTML(products, userResponses) {
    console.log(">>> generatePMRoutineHTML - Available products:", products.map(p => ({id: p.id, name: p.name, category: p.category})));
    
    // Use same personalization logic as main function
    const specialConditions = userResponses.special_conditions;
    const isSensitiveSkin = (Array.isArray(specialConditions) ? specialConditions.includes('sensitive') : specialConditions === 'sensitive') || 
                           userResponses.primary_concerns === 'sensitivity';
    const isRetinoidBeginner = Array.isArray(specialConditions) ? specialConditions.includes('retinoid') : specialConditions === 'retinoid';
    const primaryConcerns = userResponses.primary_concerns;
    
    // Categorize products correctly - Triphala is a serum, not treatment
    const cleansers = products.filter(p => 
        p.category?.toLowerCase().includes('cleans') || 
        p.name?.toLowerCase().includes('cleanser')
    );
    
    const serums = products.filter(p => 
        p.name?.toLowerCase().includes('serum') || 
        p.name?.toLowerCase().includes('resilience') ||
        p.name?.toLowerCase().includes('triphala') // Triphala is a serum
    );
    
    const treatments = products.filter(p => 
        (p.category?.toLowerCase().includes('treat') && 
         !p.name?.toLowerCase().includes('serum') && 
         !p.name?.toLowerCase().includes('resilience') &&
         !p.name?.toLowerCase().includes('triphala')) ||
        p.category?.toLowerCase().includes('exfoliat') ||
        p.name?.toLowerCase().includes('accelerator') ||
        p.name?.toLowerCase().includes('overnight')
    );
    
    const moisturizers = products.filter(p => 
        p.category?.toLowerCase().includes('moistur') ||
        p.step === 5 || // Step 5 is specifically for moisturizers
        (p.category?.toLowerCase() === 'moisturize') // Exact match for Future Veil category
    );
    
    console.log(">>> PM Products found:");
    console.log("Cleansers:", cleansers.map(p => p.name));
    console.log("Serums:", serums.map(p => p.name));
    console.log("Treatments:", treatments.map(p => p.name));
    
    let routineHTML = `
        <div class="routine-timeline">
            <div class="timeline-line pm"></div>
            <h3>🌙 Your Evening Routine</h3>
    `;

    let stepNumber = 1;

    // Step 1: Double Cleanse
    if (cleansers.length > 0) {
        const stepId = 'pm-cleanse';
        routineHTML += createTimelineStep(stepNumber, stepId, 'Double Cleanse', 
            'Start with a thorough cleanse to remove the day\'s impurities.',
            createCombinedCleanserBox(cleansers),
            '#6f42c1'
        );
        stepNumber++;
    }

    // Step 2: Pro Resilience Serum
    if (serums.length > 0) {
        const proResilience = serums.find(s => s.name?.toLowerCase().includes('pro resilience'));
        
        if (proResilience) {
            const stepId = 'pm-pro-resilience';
            let stepDescription = `Prep your skin with barrier support for evening actives.`;
            routineHTML += createTimelineStep(stepNumber, stepId, 'Strengthen & Prep', 
                stepDescription,
                createEnhancedProductCard(proResilience, 'Apply 1-2 pumps to clean, dry skin. Gently pat in until absorbed.'),
                '#6f42c1'
            );
            stepNumber++;
        }
    }

    // Step 3: Triphala Pigmentation Corrector
    const triphalaSerum = serums.find(s => s.name?.toLowerCase().includes('triphala'));
    if (triphalaSerum) {
        const stepId = 'pm-triphala';
        let stepContent = createEnhancedProductCard(triphalaSerum, 'Apply 1-2 pumps. Let it fully absorb before moving to the next step.');
        
        if (isSensitiveSkin) {
            stepContent += `
                <div class="routine-guide-tips-section">
                    <div class="routine-guide-tips-title">💡 Tips for Sensitive Skin:</div>
                    <ul class="routine-guide-tips-list">
                        <li>If your skin is feeling sensitive, you can layer the Triphala Pigmentation Corrector over your moisturiser.</li>
                        <li>For sensitive skin, start by using once daily, 2–3 times per week. Gradually increase frequency as your skin builds tolerance.</li>
                    </ul>
                </div>
            `;
        }
        
        routineHTML += createTimelineStep(stepNumber, stepId, 'Target Your Concerns', 
            'Apply your pigmentation corrector to work on dark spots and uneven tone overnight.',
            stepContent,
            '#6f42c1'
        );
        stepNumber++;
    }

    // Step 4: Overnight Treatments (if any)
    const overnightTreatments = treatments.filter(t => 
        t.name?.toLowerCase().includes('overnight') || 
        t.name?.toLowerCase().includes('retinal') ||
        t.name?.toLowerCase().includes('ursolic') ||
        t.name?.toLowerCase().includes('accelerator')
    );
    
    if (overnightTreatments.length > 0) {
        const stepId = 'pm-overnight';
        let stepContent = '';
        
        const accelerator = overnightTreatments.find(t => t.name?.toLowerCase().includes('accelerator'));
        const retinalTreatment = overnightTreatments.find(t => t.name?.toLowerCase().includes('ursolic') || t.name?.toLowerCase().includes('retinal'));
        
        if (accelerator && retinalTreatment) {
            // Both treatments available - show as options
            stepContent += `
                <div class="routine-guide-treatment-options">
                                      
                    <div class="routine-guide-treatment-option">
                        ${createEnhancedProductCard(accelerator, 'Use as a flash mask once a week for 15-20 minutes, then rinse off. Gradually increase to twice a week and slowly transition to overnight if comfortable.')}
                    </div>
                    
                    <div class="routine-guide-treatment-separator">
                        <span>or</span>
                    </div>
                    
                    <div class="routine-guide-treatment-option">
                        ${createEnhancedProductCard(retinalTreatment, 'On a different night, apply once a week to support collagen production and target fine lines. Gradually increase to two nights a week.')}
                    </div>
                </div>
            `;
        } else if (accelerator) {
            // Only accelerator available
            stepContent += createEnhancedProductCard(accelerator, 'Use as a flash mask once a week for 15-20 minutes, then rinse off. Gradually increase to twice a week and slowly transition to overnight if comfortable.');
        } else if (retinalTreatment) {
            // Only retinal treatment available
            stepContent += createEnhancedProductCard(retinalTreatment, 'Apply once a week to support collagen production and target fine lines. Gradually increase to two nights a week.');
        }

        // Add retinoid tips if any retinal products are present AND user is a retinoid beginner or has sensitive skin
        const hasRetinalProducts = overnightTreatments.some(t => t.name?.toLowerCase().includes('retinal') || t.name?.toLowerCase().includes('ursolic'));
        if (hasRetinalProducts && (isRetinoidBeginner || isSensitiveSkin)) {
            stepContent += `
                <div class="routine-guide-tips-section">
                    <div class="routine-guide-tips-title">💡 Tips for ${isSensitiveSkin ? 'Sensitive Skin' : ''}${isSensitiveSkin && isRetinoidBeginner ? ' & ' : ''}${isRetinoidBeginner ? 'Retinoid Beginner' : ''}:</div>
                    <ul class="routine-guide-tips-list">
                        ${isRetinoidBeginner ? '<li>For retinoid beginners, avoid exfoliating for 2-3 weeks prior to use and apply 1-2 times a week before building up frequency.</li>' : ''}
                        ${isRetinoidBeginner ? '<li>Allow skin to acclimatise before adding any other skin stimulating ingredients.</li>' : ''}
                        <li>You may also apply retinol products between two thin moisturising layers to minimise sensitivity.</li>
                    </ul>
                </div>
            `;
        }
        
        routineHTML += createTimelineStep(stepNumber, stepId, '🌙 Overnight Treatment', 
            'Choose a targeted overnight treatment to work while you sleep.',
            stepContent, 
            '#6f42c1'
        );
        stepNumber++;
    }

    // Final step: Moisturise
    if (moisturizers.length > 0) {
        let stepContent = '';
        
        // Display all moisturizer products
        moisturizers.forEach(moisturizer => {
            const applicationTips = moisturizer.name?.toLowerCase().includes('future veil') || moisturizer.name?.toLowerCase().includes('peptide') 
                ? 'Apply 1-2 pumps evenly across the face, and one pump to the neck and décolleté. Gently massage until absorbed. This peptide-rich formula works overnight to repair and strengthen your skin.'
                : 'Apply to clean skin and gently massage until absorbed to lock in all the benefits.';
            
            stepContent += createEnhancedProductCard(moisturizer, applicationTips);
        });
        
        routineHTML += createTimelineStep(stepNumber, 'pm-moisturise', '💧 Moisturise', 
            'Lock in all your treatments and repair overnight.',
            stepContent,
            '#6f42c1'
        );
    } else {
        // Fallback if no moisturizer products
        routineHTML += createTimelineStep(stepNumber, 'pm-moisturise', '💧 Moisturise', 
            'Follow with a moisturiser if needed to lock in all the benefits.',
            '',
            '#6f42c1'
        );
    }

    routineHTML += '</div>';
    return routineHTML;
}

/**
 * Creates a timeline step HTML
 * @param {number} stepNumber - Step number
 * @param {string} stepId - Unique step identifier
 * @param {string} title - Step title
 * @param {string} description - Step description
 * @param {string} content - Step content (products, etc.)
 * @param {string} color - Step color (am/pm indicator)
 * @returns {string} HTML for timeline step
 */
function createTimelineStep(stepNumber, stepId, title, description, content, color) {
    const isPM = color === '#6f42c1';
    return `
        <div class="timeline-step" id="${stepId}" style="--animation-order: ${stepNumber}">
            <div class="step-number ${isPM ? 'pm' : ''}">Step ${stepNumber}</div>
            <div class="step-content">
                <h4>${title}</h4>
                ${description ? `<p>${description}</p>` : ''}
                ${content ? `<div class="recommended-products">${content}</div>` : ''}
            </div>
        </div>
    `;
}

/**
 * Routine Guide Module
 * Handles all routine guide functionality including AM/PM routines, 
 * product cards, customization, and interactive features
 */

/**
 * Handle add to cart for routine guide products
 * @param {Object} product - Product object to add to cart
 * @param {Event} event - Click event
 */
function handleRoutineGuideAddToCart(product, event) {
    // Check if the main addSingleProductToCart function exists
    if (typeof addSingleProductToCart === 'function') {
        addSingleProductToCart(product, event);
    } else {
        console.error('addSingleProductToCart function not available');
        console.log('Attempting fallback cart method...');
        
        // Fallback method using the same logic as main-routinefinder.js
        const button = event ? event.currentTarget : null;
        
        // Check if product is available
        if (product.available === false) {
            console.log(`Product ${product.name} is not available for purchase`);
            if (typeof showToast === 'function') {
                showToast(`${product.name} is currently out of stock.`);
            }
            return;
        }
        
        const variantId = product.shopifyVariantId || (button ? button.getAttribute('data-variant-id') : null) || product.id;

        if (!variantId) {
            console.error(`Could not determine variant ID for product: ${product.name}`);
            if (typeof showToast === 'function') {
                showToast("Error: Could not add product variant.");
            }
            return;
        }

        console.log(`Adding product to cart: ${product.name}, variant ID: ${variantId}`);

        if (button) {
            button.disabled = true;
            button.innerHTML = '<span class="spinner-small"></span> Adding...';
        }

        if (window.shopifyHelpers && typeof window.shopifyHelpers.addToShopifyCart === 'function') {
            window.shopifyHelpers.addToShopifyCart([{ id: variantId, quantity: 1 }])
                .then(() => {
                    if (typeof showToast === 'function') {
                        showToast(`${product.name} added to your cart.`);
                    }
                    
                    // Update cart note if function exists
                    if (typeof setCartNote === 'function') {
                        setCartNote([product]);
                    }

                    if (button) {
                        button.innerHTML = '<span class="checkmark">✓</span> Added';
                        setTimeout(() => {
                            button.innerHTML = 'Add to Cart';
                            button.disabled = false;
                        }, 2000);
                    }
                })
                .catch(error => {
                    console.error("Error adding product to cart:", error);
                    if (typeof showToast === 'function') {
                        showToast(`Could not add ${product.name} to cart. Please try again.`);
                    }
                    if (button) {
                        button.innerHTML = 'Add to Cart';
                        button.disabled = false;
                    }
                });
        } else {
            console.error("Shopify cart helper not available.");
            if (typeof showToast === 'function') {
                showToast("Error: Cart functionality is unavailable.");
            }
            if (button) {
                button.innerHTML = 'Add to Cart';
                button.disabled = false;
            }
        }
    }
}

// Make functions globally available
window.routineGuide = {
    setupRoutineGuideSubTabs,
    generateRoutineGuideContent
};
